import { useState, useEffect, useMemo, useRef } from 'react';
import {
    InformationCircleIcon,
    TrashIcon,
    QrCodeIcon,
    CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { useAppDispatch, useAppSelector } from '../../../shared/store/hooks';
import {
    settingsSliceAPI,
    clearError,
    setGeneralSettingsFormDraft,
    initialGeneralSettingsFormDraft,
    defaultSocialPlatformsEnabledFromForm,
    SOCIAL_PLATFORM_KEYS,
} from '../store/settingsSlice';
import { extractErrorMessage } from '../../../shared/utils/errorHandling';
import { normalizeUrl } from '../../../shared/utils/urlUtils';
import { validateAddress } from '../../../shared/utils/addressValidator';
import { createLogger } from '../../../shared/config/logger';
import { resendMerchantSignupQrEmail, postAccountDeletionRequest } from '../services/generalSettingsApi';
import { getMerchantCategorySelectOptions } from '../../../shared/constants/merchantBusinessCategories';

const logger = createLogger('GeneralSettings');

const SOCIAL_OPTIONS = [
    { value: 'facebook_url', label: 'Facebook' },
    { value: 'instagram_url', label: 'Instagram' },
    { value: 'twitter_url', label: 'X (Twitter)' },
    { value: 'linkedin_url', label: 'LinkedIn' },
];

function socialUrlPlaceholder(value) {
    switch (value) {
        case 'facebook_url':
            return 'www.facebook.com/yourpage';
        case 'instagram_url':
            return 'www.instagram.com/yourpage';
        case 'twitter_url':
            return 'x.com/yourhandle';
        case 'linkedin_url':
            return 'www.linkedin.com/company/yourpage';
        default:
            return '';
    }
}

function resolveSocialPlatformsEnabled(form, draft) {
    const base = defaultSocialPlatformsEnabledFromForm(form);
    if (!draft?.socialPlatformsEnabled) {
        return base;
    }
    return { ...base, ...draft.socialPlatformsEnabled };
}

function mapGeneralSettingsToForm(generalsettings) {
    const defaults = initialGeneralSettingsFormDraft().form;
    if (!generalsettings || Object.keys(generalsettings).length === 0) {
        return { ...defaults };
    }
    return {
        name: generalsettings.name || '',
        phone: generalsettings.phone || '',
        category: (generalsettings.category || '').trim(),
        address: generalsettings.address || '',
        website: (generalsettings.website || '').replace(/^https?:\/\//i, '').trim() || '',
        review_link: (generalsettings.review_link || '').replace(/^https?:\/\//i, '').trim() || '',
        facebook_url: (generalsettings.facebook_url || '').replace(/^https?:\/\//i, '').trim() || '',
        instagram_url: (generalsettings.instagram_url || '').replace(/^https?:\/\//i, '').trim() || '',
        twitter_url: (generalsettings.twitter_url || '').replace(/^https?:\/\//i, '').trim() || '',
        linkedin_url: (generalsettings.linkedin_url || '').replace(/^https?:\/\//i, '').trim() || '',
        brand_color: generalsettings.brand_color || defaults.brand_color,
        text_color: generalsettings.text_color || defaults.text_color,
        secondary_color: generalsettings.secondary_color || defaults.secondary_color,
        logo_url: (generalsettings.logo_url || '').trim() || '',
    };
}

export default function GeneralSettings() {
    const dispatch = useAppDispatch();
    const generalsettings = useAppSelector((state) => state.settings.generalsettings);
    const generalSettingsFormDraft = useAppSelector((state) => state.settings.generalSettingsFormDraft);
    const generalSettingsFetchStatus = useAppSelector((state) => state.settings.generalSettingsFetchStatus);
    const loading = useAppSelector((state) => state.settings.loading);
    const error = useAppSelector((state) => state.settings.error);

    const form = useMemo(() => {
        if (generalSettingsFormDraft?.form) {
            return generalSettingsFormDraft.form;
        }
        return mapGeneralSettingsToForm(generalsettings);
    }, [generalSettingsFormDraft, generalsettings]);

    const [message, setMessage] = useState(null);
    const [messageType, setMessageType] = useState('');
    const [errors, setErrors] = useState({});
    const [validatingAddress, setValidatingAddress] = useState(false);
    const [resendQrLoading, setResendQrLoading] = useState(false);
    const [resendQrMessage, setResendQrMessage] = useState(null);
    const [resendQrMessageType, setResendQrMessageType] = useState('');
    const [socialSelectValue, setSocialSelectValue] = useState('');
    const [showDeletionModal, setShowDeletionModal] = useState(false);
    const [deletionNote, setDeletionNote] = useState('');
    const [deletionLoading, setDeletionLoading] = useState(false);
    const [deletionMessage, setDeletionMessage] = useState(null);
    const [deletionMessageType, setDeletionMessageType] = useState('');
    const socialInputRefs = useRef({});
    const pendingSocialFocusRef = useRef(null);

    const socialPlatformsEnabled = useMemo(
        () => resolveSocialPlatformsEnabled(form, generalSettingsFormDraft),
        [form, generalSettingsFormDraft]
    );

    const merchantCategoryOptions = useMemo(() => getMerchantCategorySelectOptions(form.category), [form.category]);

    const handleSocialToggle = (key, checked) => {
        const next = { ...socialPlatformsEnabled, [key]: checked };
        const formPatch = checked ? {} : { [key]: '' };
        if (generalSettingsFormDraft) {
            dispatch(
                setGeneralSettingsFormDraft({
                    socialPlatformsEnabled: next,
                    ...(Object.keys(formPatch).length > 0 ? { form: formPatch } : {}),
                })
            );
        } else {
            dispatch(
                setGeneralSettingsFormDraft({
                    form: { ...form, ...formPatch },
                    socialPlatformsEnabled: next,
                })
            );
        }
    };

    const handlePickSocialPlatform = (key) => {
        if (socialPlatformsEnabled[key]) {
            queueMicrotask(() => socialInputRefs.current[key]?.focus());
            return;
        }
        pendingSocialFocusRef.current = key;
        handleSocialToggle(key, true);
    };

    const handleSocialDropdownChange = (e) => {
        const value = e.target.value;
        if (value) {
            handlePickSocialPlatform(value);
        }
        setSocialSelectValue('');
    };

    useEffect(() => {
        const k = pendingSocialFocusRef.current;
        if (!k || !socialPlatformsEnabled[k]) {
            return;
        }
        socialInputRefs.current[k]?.focus();
        pendingSocialFocusRef.current = null;
    }, [socialPlatformsEnabled]);

    /**
     * Keep unsaved General settings in Redux (`generalSettingsFormDraft`) as soon as server data is ready.
     * Otherwise the form reads live from `generalsettings`, which is replaced whenever any screen refetches —
     * that wipes in-progress edits when switching browser tabs or settings sub-routes.
     */
    useEffect(() => {
        if (generalSettingsFormDraft) {
            return;
        }
        if (generalSettingsFetchStatus !== 'succeeded' && generalSettingsFetchStatus !== 'failed') {
            return;
        }
        dispatch(
            setGeneralSettingsFormDraft({
                form: mapGeneralSettingsToForm(generalsettings),
            })
        );
    }, [dispatch, generalSettingsFormDraft, generalSettingsFetchStatus, generalsettings]);

    useEffect(() => {
        if (generalSettingsFetchStatus === 'idle') {
            dispatch(settingsSliceAPI.fetchGeneralSettings());
        }
    }, [dispatch, generalSettingsFetchStatus]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (generalSettingsFormDraft) {
            dispatch(setGeneralSettingsFormDraft({ form: { [name]: value } }));
        } else {
            dispatch(setGeneralSettingsFormDraft({ form: { ...form, [name]: value } }));
        }
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage(null);
        setMessageType('');
        setErrors({});
        dispatch(clearError());

        const newErrors = {};

        const name = form.name.trim();
        const phone = form.phone.trim();
        const address = form.address.trim();
        const website = normalizeUrl(form.website);
        const review_link = normalizeUrl(form.review_link);

        // Store name required
        if (!name) {
            newErrors.name = 'Store name is required.';
        }

        // Phone optional, but if present validate format
        if (phone && !/^\+?[0-9\s\-().]{7,20}$/.test(phone)) {
            newErrors.phone = 'Enter a valid phone number.';
        }

        // Address validation - check if it exists
        if (address) {
            setValidatingAddress(true);
            try {
                const addressValidation = await validateAddress(address);
                if (!addressValidation.valid) {
                    newErrors.address = addressValidation.error || 'Please enter a valid address.';
                }
            } catch (err) {
                // If validation fails due to network error, allow submission
                // Backend will handle it gracefully
                logger.warn('Address validation error', { error: err });
            } finally {
                setValidatingAddress(false);
            }
        }

        // Website optional but must be valid if present (normalizeUrl adds https://)
        if (website) {
            try {
                new URL(website);
            } catch {
                newErrors.website = 'Enter a valid website (e.g. yourstore.com).';
            }
        }

        // Google Maps link optional but must be a valid URL if present
        if (review_link) {
            try {
                new URL(review_link);
            } catch {
                newErrors.review_link = 'Enter a valid Google Maps URL.';
            }
        }

        const normalizeOptionalUrl = (val) => (val?.trim() ? normalizeUrl(val.trim()) : '');
        const enabledMap = resolveSocialPlatformsEnabled(form, generalSettingsFormDraft);

        SOCIAL_PLATFORM_KEYS.forEach((key) => {
            if (!enabledMap[key]) {
                return;
            }
            const url = normalizeOptionalUrl(form[key]);
            if (!url) {
                const label = SOCIAL_OPTIONS.find((o) => o.value === key)?.label || key;
                newErrors[key] = `Enter your ${label} link or turn it off.`;
                return;
            }
            try {
                new URL(url);
            } catch {
                newErrors[key] = 'Enter a valid URL.';
            }
        });

        const logoRaw = form.logo_url?.trim() || '';
        let logo_url = '';
        if (!logoRaw) {
            newErrors.logo_url = 'Logo URL is required.';
        } else {
            logo_url = normalizeUrl(logoRaw);
            try {
                const logoParsed = new URL(logo_url);
                if (logoParsed.protocol !== 'http:' && logoParsed.protocol !== 'https:') {
                    newErrors.logo_url = 'Logo URL must use http or https.';
                }
            } catch {
                newErrors.logo_url = 'Enter a valid logo URL (e.g. https://yoursite.com/logo.png).';
            }
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        const payload = {
            ...generalsettings,
            ...form,
            name,
            phone,
            address,
            website,
            review_link,
            facebook_url: enabledMap.facebook_url ? normalizeOptionalUrl(form.facebook_url) || null : null,
            instagram_url: enabledMap.instagram_url ? normalizeOptionalUrl(form.instagram_url) || null : null,
            twitter_url: enabledMap.twitter_url ? normalizeOptionalUrl(form.twitter_url) || null : null,
            linkedin_url: enabledMap.linkedin_url ? normalizeOptionalUrl(form.linkedin_url) || null : null,
            logo_url,
            category: (form.category || '').trim(),
        };

        try {
            await dispatch(settingsSliceAPI.updateGeneralSettings(payload)).unwrap();
            setMessage('Settings saved successfully!');
            setMessageType('success');
        } catch (err) {
            dispatch(clearError());
            setMessage(extractErrorMessage(err) || 'Failed to save settings.');
            setMessageType('error');
        }
    };

    const handleResendSignupQr = async () => {
        setResendQrMessage(null);
        setResendQrMessageType('');
        setResendQrLoading(true);
        try {
            const res = await resendMerchantSignupQrEmail();
            setResendQrMessage(res.data?.message || 'Email sent. Check your inbox.');
            setResendQrMessageType('success');
        } catch (err) {
            setResendQrMessage(extractErrorMessage(err) || 'Could not send email.');
            setResendQrMessageType('error');
        } finally {
            setResendQrLoading(false);
        }
    };

    const handleConfirmAccountDeletionRequest = async () => {
        setDeletionLoading(true);
        setDeletionMessage(null);
        setDeletionMessageType('');
        try {
            const res = await postAccountDeletionRequest({
                message: deletionNote.trim() || undefined,
            });
            setDeletionMessage(res.data?.message || 'Request sent.');
            setDeletionMessageType('success');
            setShowDeletionModal(false);
            setDeletionNote('');
        } catch (err) {
            setDeletionMessage(extractErrorMessage(err) || 'Could not send request.');
            setDeletionMessageType('error');
        } finally {
            setDeletionLoading(false);
        }
    };

    return (
        <>
            {(message || error) && (
                <div
                    className="pointer-events-none fixed right-4 top-4 z-[110] flex w-[min(24rem,calc(100vw-2rem))] flex-col items-stretch gap-2 sm:right-6 sm:top-6"
                    aria-label="Notifications"
                >
                    {message && (
                        <div
                            role={messageType === 'error' ? 'alert' : 'status'}
                            aria-live={messageType === 'error' ? 'assertive' : 'polite'}
                            className={`pointer-events-auto rounded-md border p-3 text-sm shadow-lg ${
                                messageType === 'success'
                                    ? 'border-green-200 bg-green-100 text-green-800'
                                    : 'border-red-300 bg-red-50 text-red-900'
                            }`}
                        >
                            {messageType === 'error' ? (
                                <>
                                    <p className="font-semibold">Couldn&apos;t save settings</p>
                                    <p className="mt-1">{message}</p>
                                </>
                            ) : (
                                <p className="flex items-center gap-2 font-medium">
                                    <CheckCircleIcon className="h-5 w-5 shrink-0" aria-hidden />
                                    <span>{message}</span>
                                </p>
                            )}
                        </div>
                    )}
                    {error && (
                        <div
                            role="alert"
                            aria-live="assertive"
                            className="pointer-events-auto rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900 shadow-lg"
                        >
                            <p className="font-semibold">Something went wrong</p>
                            <p className="mt-1">{typeof error === 'string' ? error : JSON.stringify(error)}</p>
                        </div>
                    )}
                </div>
            )}

            <div className="flex flex-col p-6 gap-4">
            <h2 className="text-2xl font-bold text-primary mb-4">General Settings</h2>
            <div className="mb-2 flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                <InformationCircleIcon
                    className="h-5 w-5 shrink-0 mt-0.5 text-amber-700"
                    aria-hidden
                />
                <span>
                    <strong>Note:</strong> The information you enter here (store name, address,
                    branding, etc.) will be used when generating your Apple and Android passes.{' '}
                    <span className="font-medium text-amber-950">
                        Once you create a pass template, modifying it is <u>currently not supported</u>.
                        Any changes you make here after creating the Android/iOS pass templates will{' '}
                        <u>not</u> be reflected in those templates.
                    </span>
                </span>
            </div>
            <p className="text-gray-600">Update your store information below</p>

            {loading && <div className="p-3 rounded-md bg-blue-100 text-blue-700">Loading...</div>}

            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <div>
                    <label
                        htmlFor="merchantName"
                        className="block text-sm font-medium text-gray-700 mb-1"
                    >
                        Store Name
                    </label>
                    <input
                        id="merchantName"
                        name="name"
                        type="text"
                        value={form.name}
                        onChange={handleChange}
                        placeholder="Your Store's name"
                        className="w-full p-3 border border-gray-300 rounded-md"
                        disabled={loading}
                    />
                </div>
                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}

                <div>
                    <label
                        htmlFor="merchantCategory"
                        className="block text-sm font-medium text-gray-700 mb-1"
                    >
                        Business Category
                    </label>
                    <select
                        id="merchantCategory"
                        name="category"
                        value={form.category}
                        onChange={handleChange}
                        className="w-full rounded-md border border-gray-300 bg-white p-3 text-gray-900"
                        disabled={loading}
                    >
                        <option value="">— Select category —</option>
                        {merchantCategoryOptions.map((opt) => (
                            <option key={opt} value={opt}>
                                {opt}
                            </option>
                        ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                        Pick the option that best describes your business. If your saved category is not in the list, it
                        appears as its own option until you choose a standard one.
                    </p>
                </div>

                <div>
                    <label
                        htmlFor="merchantPhoneNumber"
                        className="block text-sm font-medium text-gray-700 mb-1"
                    >
                        Phone Number
                    </label>
                    <input
                        id="merchantPhoneNumber"
                        name="phone"
                        type="tel"
                        inputMode="tel"
                        value={form.phone}
                        maxLength={10}
                        onChange={handleChange}
                        placeholder="Your store's phone number"
                        className="w-full p-3 border border-gray-300 rounded-md"
                        disabled={loading}
                        aria-invalid={!!errors.phone}
                    />
                </div>
                {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}

                <div>
                    <label
                        htmlFor="merchantAddress"
                        className="block text-sm font-medium text-gray-700 mb-1"
                    >
                        Store Address
                    </label>
                    <input
                        id="merchantAddress"
                        name="address"
                        type="text"
                        value={form.address}
                        onChange={handleChange}
                        placeholder="Your store's address"
                        className={`w-full p-3 border rounded-md ${
                            errors.address ? 'border-red-300' : 'border-gray-300'
                        }`}
                        disabled={loading || validatingAddress}
                        aria-invalid={!!errors.address}
                    />
                    {validatingAddress && (
                        <p className="text-blue-600 text-sm mt-1">Validating address...</p>
                    )}
                    {errors.address && (
                        <p className="text-red-500 text-sm mt-1">{errors.address}</p>
                    )}
                </div>

                <div>
                    <label
                        htmlFor="merchantWebsite"
                        className="block text-sm font-medium text-gray-700 mb-1"
                    >
                        Website
                    </label>
                    <input
                        id="merchantWebsite"
                        name="website"
                        type="text"
                        value={form.website}
                        onChange={handleChange}
                        placeholder="yourstore.com"
                        className="w-full p-3 border border-gray-300 rounded-md"
                        disabled={loading}
                        aria-invalid={!!errors.website}
                    />
                    <p className="text-gray-500 text-xs mt-1">Enter without https:// (e.g. yourstore.com)</p>
                </div>
                {errors.website && <p className="text-red-500 text-sm mt-1">{errors.website}</p>}

                <div>
                    <label
                        htmlFor="merchantGoogleMapsLink"
                        className="block text-sm font-medium text-gray-700 mb-1"
                    >
                        Google Maps link
                    </label>
                    <input
                        id="merchantGoogleMapsLink"
                        name="review_link"
                        type="text"
                        value={form.review_link}
                        onChange={handleChange}
                        placeholder="www.google.com/maps/place/Your+Business"
                        className="w-full p-3 border border-gray-300 rounded-md"
                        disabled={loading}
                        aria-invalid={!!errors.review_link}
                    />
                    <p className="text-gray-500 text-xs mt-1">Enter without https:// (e.g. www.google.com/maps/place/...)</p>
                </div>
                {errors.review_link && (
                    <p className="text-red-500 text-sm mt-1">{errors.review_link}</p>
                )}

                <div>
                    <h3 className="text-xl font-semibold mb-2">Social links</h3>
                    <p className="text-gray-600 text-sm mb-3">
                        Used in the Visibility Checker. Choose a platform from the dropdown to add or edit your
                        profile link (without https://). We currently support {SOCIAL_OPTIONS.length} networks; more
                        may be added later.
                    </p>

                    <div className="mb-4">
                        <label
                            htmlFor="social-platform-select"
                            className="mb-1 block text-sm font-medium text-gray-700"
                        >
                            Add or edit a platform
                        </label>
                        <select
                            id="social-platform-select"
                            value={socialSelectValue}
                            onChange={handleSocialDropdownChange}
                            disabled={loading}
                            className="w-full rounded-md border border-gray-300 bg-white p-3 text-gray-900"
                        >
                            <option value="">— Select Socials —</option>
                            {SOCIAL_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                    {socialPlatformsEnabled[opt.value] ? ' (edit link)' : ''}
                                </option>
                            ))}
                        </select>
                        <p className="mt-1 text-xs text-gray-500">
                            Selecting a network adds it below (or focuses its URL field if it is already added).
                        </p>
                    </div>

                    {SOCIAL_OPTIONS.some((o) => socialPlatformsEnabled[o.value]) ? (
                        <div className="space-y-4">
                            <h4 className="text-sm font-semibold text-gray-800">Your social links</h4>
                            {SOCIAL_OPTIONS.filter((o) => socialPlatformsEnabled[o.value]).map((opt) => (
                                <div
                                    key={opt.value}
                                    className="rounded-lg border border-gray-200 bg-gray-50/80 p-3 sm:p-4"
                                >
                                    <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                                        <span className="text-sm font-medium text-gray-800">{opt.label}</span>
                                        <button
                                            type="button"
                                            onClick={() => handleSocialToggle(opt.value, false)}
                                            disabled={loading}
                                            className="text-xs font-medium text-red-600 hover:text-red-700 hover:underline"
                                        >
                                            Remove
                                        </button>
                                    </div>
                                    <label htmlFor={`social-${opt.value}`} className="sr-only">
                                        {opt.label} profile URL
                                    </label>
                                    <input
                                        id={`social-${opt.value}`}
                                        ref={(el) => {
                                            socialInputRefs.current[opt.value] = el;
                                        }}
                                        name={opt.value}
                                        type="text"
                                        value={form[opt.value] || ''}
                                        onChange={handleChange}
                                        placeholder={socialUrlPlaceholder(opt.value)}
                                        className={`w-full rounded-md border p-3 ${
                                            errors[opt.value] ? 'border-red-300' : 'border-gray-300'
                                        }`}
                                        disabled={loading}
                                        aria-invalid={!!errors[opt.value]}
                                    />
                                    {errors[opt.value] && (
                                        <p className="mt-1 text-sm text-red-500">{errors[opt.value]}</p>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-sm text-gray-500">
                            Choose a platform from the dropdown above to add your profile URL. All social fields are
                            optional.
                        </p>
                    )}
                </div>

                <div>
                    <h3 className="text-xl font-semibold mb-4">Branding</h3>
                    <p className="text-sm text-gray-600 mb-3">Used for marketing emails, SMS, and pass templates.</p>
                    <div className="mb-4">
                        <label
                            htmlFor="merchantLogoUrl"
                            className="block font-medium mb-1 text-gray-700"
                        >
                            Logo URL <span className="text-red-600">*</span>
                        </label>
                        <input
                            id="merchantLogoUrl"
                            name="logo_url"
                            type="text"
                            required
                            value={form.logo_url}
                            onChange={handleChange}
                            placeholder="https://yoursite.com/logo.png"
                            className={`w-full p-3 border rounded-md ${
                                errors.logo_url ? 'border-red-300' : 'border-gray-300'
                            }`}
                            disabled={loading}
                            aria-invalid={!!errors.logo_url}
                            aria-describedby="merchantLogoUrl-hint"
                        />
                        {errors.logo_url && (
                            <p className="text-red-500 text-sm mt-1">{errors.logo_url}</p>
                        )}
                        <p id="merchantLogoUrl-hint" className="text-gray-500 text-xs mt-1">
                            Full URL to your logo image (https://…). Required for Android and Apple pass templates.
                        </p>
                        {form.logo_url && (
                            <div className="mt-2">
                                <img
                                    src={form.logo_url.trim().match(/^https?:\/\//i) ? form.logo_url : `https://${form.logo_url.trim()}`}
                                    alt="Logo preview"
                                    className="h-16 w-auto object-contain rounded border border-gray-200"
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                />
                            </div>
                        )}
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4 items-start">
                        <div>
                            <label className="block font-medium mb-1">Primary color</label>
                            <input
                                type="color"
                                name="brand_color"
                                value={form.brand_color}
                                onChange={handleChange}
                                className="w-16 h-10 border rounded"
                            />
                        </div>
                        <div>
                            <label className="block font-medium mb-1">Secondary color</label>
                            <input
                                type="color"
                                name="secondary_color"
                                value={form.secondary_color}
                                onChange={handleChange}
                                className="w-16 h-10 border rounded"
                            />
                        </div>
                        <div>
                            <label className="block font-medium mb-1">Text color</label>
                            <input
                                type="color"
                                name="text_color"
                                value={form.text_color}
                                onChange={handleChange}
                                className="w-16 h-10 border rounded"
                            />
                        </div>
                    </div>
                </div>

                <button
                    type="submit"
                    className="bg-primary text-white py-3 px-6 rounded-md hover:bg-purple-400 transition w-auto self-center mt-2"
                    disabled={loading}
                >
                    {loading ? 'Saving...' : 'Save'}
                </button>
            </form>

            <div className="mt-3 flex flex-col gap-3 md:flex-row md:items-stretch md:gap-4">
                <div className="min-w-0 flex-1 rounded-md border border-gray-200 bg-gray-50 p-3">
                    <div className="flex items-start gap-2">
                        <TrashIcon className="h-5 w-5 shrink-0 text-gray-500 mt-0.5" aria-hidden />
                        <div className="min-w-0 flex-1">
                            <h3 className="text-sm font-semibold text-gray-800">Account deletion</h3>
                            <p className="mt-1 text-xs leading-relaxed text-gray-600">
                                To close your merchant account, send a request. Our team will verify and process the
                                deletion.
                            </p>
                            {deletionMessage && (
                                <div
                                    className={`mt-2 rounded px-2 py-1.5 text-xs ${
                                        deletionMessageType === 'success'
                                            ? 'bg-green-100 text-green-800'
                                            : 'bg-red-100 text-red-800'
                                    }`}
                                >
                                    {deletionMessage}
                                </div>
                            )}
                            <button
                                type="button"
                                onClick={() => setShowDeletionModal(true)}
                                disabled={deletionLoading}
                                className="mt-2 rounded border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 transition hover:bg-red-50 disabled:opacity-60"
                            >
                                Request account deletion
                            </button>
                        </div>
                    </div>
                </div>

                <div className="min-w-0 flex-1 rounded-md border border-gray-200 bg-gray-50 p-3">
                    <div className="flex items-start gap-2">
                        <QrCodeIcon className="h-5 w-5 shrink-0 text-gray-500 mt-0.5" aria-hidden />
                        <div className="min-w-0 flex-1">
                            <h3 className="text-sm font-semibold text-gray-800">In-store customer signup</h3>
                            <p className="mt-1 text-xs leading-relaxed text-gray-600">
                                We emailed you a printable QR code when you registered. Use it at your counter so
                                customers can scan and join your loyalty program. Send it again if you lost the email.
                            </p>
                            {resendQrMessage && (
                                <div
                                    className={`mt-2 rounded px-2 py-1.5 text-xs ${
                                        resendQrMessageType === 'success'
                                            ? 'bg-green-100 text-green-800'
                                            : 'bg-red-100 text-red-800'
                                    }`}
                                >
                                    {resendQrMessage}
                                </div>
                            )}
                            <button
                                type="button"
                                onClick={handleResendSignupQr}
                                disabled={resendQrLoading}
                                className="mt-2 rounded bg-primary px-3 py-1.5 text-xs font-medium text-white transition hover:bg-purple-400 disabled:opacity-60"
                            >
                                {resendQrLoading ? 'Sending…' : 'Email QR code again'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {showDeletionModal && (
                <div
                    className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="deletion-modal-title"
                    onClick={() => !deletionLoading && setShowDeletionModal(false)}
                >
                    <div
                        className="max-w-md rounded-lg bg-white p-6 shadow-xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h4 id="deletion-modal-title" className="text-lg font-semibold text-gray-900">
                            Send account deletion request?
                        </h4>
                        <p className="mt-2 text-sm text-gray-600">
                            This emails LoyalPass at loyalpassapp@gmail.com with your merchant ID and login email. Your
                            account is not deleted until we confirm.
                        </p>
                        <label htmlFor="account-deletion-note-modal" className="mt-4 block text-sm font-medium text-gray-700">
                            Optional note for our team
                        </label>
                        <textarea
                            id="account-deletion-note-modal"
                            value={deletionNote}
                            onChange={(e) => setDeletionNote(e.target.value)}
                            rows={3}
                            maxLength={2000}
                            disabled={deletionLoading}
                            placeholder="e.g. reason for leaving or timeline"
                            className="mt-1 w-full rounded-md border border-gray-300 p-2 text-sm"
                        />
                        <p className="mt-1 text-xs text-gray-500">{deletionNote.length} / 2000</p>
                        <div className="mt-6 flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={() => setShowDeletionModal(false)}
                                disabled={deletionLoading}
                                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleConfirmAccountDeletionRequest}
                                disabled={deletionLoading}
                                className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                            >
                                {deletionLoading ? 'Sending…' : 'Send request'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
        </>
    );
}
